import Pako from 'pako';
import { Buffer } from 'buffer';
export const REPO_OBJECT_BATCH_SIZE = 500;
const GIT_OBJECT_HEADER_SEPARATOR = 0;
const GIT_TREE_ENTRY_SEPARATOR = 32;
const GIT_TREE_PATH_TERMINATOR = 0;
const GIT_OID_BYTE_LENGTH = 20;
const GIT_SUBMODULE_MODE = '160000';
function base64ToUint8Array(content) {
    return Uint8Array.from(atob(content), char => char.charCodeAt(0));
}
function decodeUtf8(content) {
    if (typeof TextDecoder !== 'undefined') {
        return new TextDecoder().decode(content);
    }
    return Buffer.from(content).toString('utf8');
}
function decodeGitObject(objectContent) {
    const compressed = base64ToUint8Array(objectContent.content);
    const raw = Pako.inflate(compressed);
    let headerEnd = 0;
    while (headerEnd < raw.length && raw[headerEnd] !== GIT_OBJECT_HEADER_SEPARATOR) {
        headerEnd += 1;
    }
    if (headerEnd >= raw.length) {
        throw new Error('Invalid git object header');
    }
    const header = decodeUtf8(raw.subarray(0, headerEnd));
    const separatorIndex = header.indexOf(' ');
    if (separatorIndex === -1) {
        throw new Error(`Invalid git object header: ${header}`);
    }
    return {
        type: header.slice(0, separatorIndex),
        body: raw.subarray(headerEnd + 1),
    };
}
function parseCommitChildOids(body) {
    const content = decodeUtf8(body);
    const treeMatch = content.match(/^tree ([0-9a-f]{40})$/m);
    return treeMatch ? [treeMatch[1]] : [];
}
function parseTreeChildOids(body) {
    const childOids = [];
    let offset = 0;
    while (offset < body.length) {
        let modeEnd = offset;
        while (modeEnd < body.length && body[modeEnd] !== GIT_TREE_ENTRY_SEPARATOR) {
            modeEnd += 1;
        }
        if (modeEnd >= body.length) {
            throw new Error('Invalid git tree entry mode');
        }
        const mode = decodeUtf8(body.subarray(offset, modeEnd));
        offset = modeEnd + 1;
        let pathEnd = offset;
        while (pathEnd < body.length && body[pathEnd] !== GIT_TREE_PATH_TERMINATOR) {
            pathEnd += 1;
        }
        if (pathEnd >= body.length) {
            throw new Error('Invalid git tree entry path');
        }
        offset = pathEnd + 1;
        if (offset + GIT_OID_BYTE_LENGTH > body.length) {
            throw new Error('Invalid git tree entry oid');
        }
        const oidBytes = body.subarray(offset, offset + GIT_OID_BYTE_LENGTH);
        offset += GIT_OID_BYTE_LENGTH;
        if (mode === GIT_SUBMODULE_MODE) {
            continue;
        }
        childOids.push(Array.from(oidBytes, byte => byte.toString(16).padStart(2, '0')).join(''));
    }
    return childOids;
}
function collectChildOids(objectContent) {
    const { type, body } = decodeGitObject(objectContent);
    if (type === 'commit') {
        return parseCommitChildOids(body);
    }
    if (type === 'tree') {
        return parseTreeChildOids(body);
    }
    return [];
}
/**
 * 从给定的 root commit oid 出发，按可达性 BFS 拉取 tree 闭包（commit → tree → 子 tree/blob）。
 * 注意：只遍历每个 commit 的 tree，**不会**追溯 commit 的 parent，因此返回的对象集不包含历史 commit 链。
 * 适用场景：worker 侧只需要 `git.checkout` 或递归 `readTree`/`readBlob`（当前文件的所有调用都是这种）。
 * 如需遍历 commit 历史，请在 `parseCommitChildOids` 中额外解析 `parent` 行。
 */
export async function fetchReachableRepoObjects(rootOids, readRepoObjects) {
    const objects = {};
    const queue = [];
    const seenOids = new Set();
    const enqueue = (oid) => {
        if (!oid || seenOids.has(oid)) {
            return;
        }
        seenOids.add(oid);
        queue.push(oid);
    };
    rootOids.forEach(enqueue);
    while (queue.length > 0) {
        const batch = queue.splice(0, REPO_OBJECT_BATCH_SIZE);
        const batchObjects = await readRepoObjects(batch);
        for (const oid of batch) {
            const objectContent = batchObjects[oid];
            if (!objectContent) {
                continue;
            }
            objects[oid] = objectContent;
            try {
                collectChildOids(objectContent).forEach(enqueue);
            }
            catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                throw new Error(`Failed to parse git object ${oid}: ${message}`);
            }
        }
    }
    return objects;
}
